/**
 * GLSL ES 3.00 shaders for High quality.
 *
 * Passes: (1) G-buffer prepass writing view-space normal + linear depth + view
 * position into an MRT; (2) dual-radius SSAO from that buffer; (3) a depth-aware
 * separable blur; (4) a forward shade by the scene light that multiplies the
 * blurred AO into the ambient term only. All geometry programs share attribute
 * locations 0/1/2 (position / normal / colour) so one VAO feeds every pass.
 */

import { SCENE_LIGHT_GLSL } from '../sceneLighting';

// Shared attribute preamble for geometry programs.
const GEOM_ATTRS = `
layout(location=0) in vec3 aPos;
layout(location=1) in vec3 aNormal;
layout(location=2) in vec3 aColor;`;

// --- G-buffer prepass -------------------------------------------------------
export const GBUFFER_VERT = `#version 300 es
${GEOM_ATTRS}
uniform mat4 uView;
uniform mat4 uProj;
out vec3 vViewPos;
out vec3 vViewNormal;
void main() {
  vec4 vp = uView * vec4(aPos, 1.0);
  vViewPos = vp.xyz;
  vViewNormal = mat3(uView) * aNormal;
  gl_Position = uProj * vp;
}`;

export const GBUFFER_FRAG = `#version 300 es
precision highp float;
in vec3 vViewPos;
in vec3 vViewNormal;
layout(location=0) out vec4 oNormalDepth; // .xyz view normal, .w linear view depth (-z)
layout(location=1) out vec4 oPos;         // .xyz view-space position
void main() {
  vec3 n = normalize(vViewNormal);
  if (!gl_FrontFacing) n = -n;
  oNormalDepth = vec4(n, -vViewPos.z);
  oPos = vec4(vViewPos, 1.0);
}`;

// --- Forward lit shade (samples AO) -----------------------------------------
export const LIT_VERT = `#version 300 es
${GEOM_ATTRS}
uniform mat4 uView;
uniform mat4 uProj;
out vec3 vViewNormal;
out vec3 vColor;
void main() {
  vec4 vp = uView * vec4(aPos, 1.0);
  vViewNormal = mat3(uView) * aNormal;
  vColor = aColor;
  gl_Position = uProj * vp;
}`;

export const LIT_FRAG = `#version 300 es
precision highp float;
in vec3 vViewNormal;
in vec3 vColor;
uniform sampler2D uAO;
uniform vec2 uResolution;
uniform float uAoStrength; // 0 = AO off, 1 = full
out vec4 frag;
${SCENE_LIGHT_GLSL}
void main() {
  vec3 n = normalize(vViewNormal);
  if (!gl_FrontFacing) n = -n;
  float ao = mix(1.0, texture(uAO, gl_FragCoord.xy / uResolution).r, uAoStrength);
  frag = vec4(vColor * sceneLight(n, ao), 1.0);
}`;

// --- Flat program for outlines + glass (no lighting) ------------------------
export const FLAT_VERT = `#version 300 es
${GEOM_ATTRS}
uniform mat4 uViewProj;
out vec3 vColor;
void main() {
  vColor = aColor;
  gl_Position = uViewProj * vec4(aPos, 1.0);
}`;

export const FLAT_FRAG = `#version 300 es
precision highp float;
in vec3 vColor;
uniform float uAlpha;
out vec4 frag;
void main() { frag = vec4(vColor, uAlpha); }`;

// --- Fullscreen triangle (post passes) --------------------------------------
export const FULLSCREEN_VERT = `#version 300 es
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

// --- Dual-radius SSAO -------------------------------------------------------
export const SSAO_FRAG = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uNormalDepth;
uniform sampler2D uPos;
uniform sampler2D uNoise;
uniform mat4 uProj;
uniform vec2 uNoiseScale;     // aoResolution / noiseSize
uniform vec3 uKernel[64];
uniform int uSamples;         // taps per radius
uniform float uNearRadius;
uniform float uFarRadius;
uniform float uNearWeight;
uniform float uFarWeight;
uniform float uPower;
uniform float uRangeFalloff;
out float oAO;

const float BIAS = 0.02;

// Occlusion at one radius: project each hemisphere sample to the screen, read the
// stored view position there, and count it as an occluder if it sits in front of
// the sample — weighted by a depth range-check so a much-nearer surface can't
// over-darken (the classic SSAO haloing fix).
float occlusion(vec3 P, mat3 TBN, float radius) {
  float occ = 0.0;
  for (int i = 0; i < 64; i++) {
    if (i >= uSamples) break;
    vec3 sp = P + TBN * uKernel[i] * radius;
    vec4 off = uProj * vec4(sp, 1.0);
    off.xyz /= off.w;
    vec2 uv = off.xy * 0.5 + 0.5;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) continue;
    vec3 op = texture(uPos, uv).xyz;
    float rangeCheck = smoothstep(0.0, 1.0, radius / max(1e-4, abs(P.z - op.z) * uRangeFalloff));
    occ += (op.z >= sp.z + BIAS ? 1.0 : 0.0) * rangeCheck;
  }
  return occ / float(uSamples);
}

void main() {
  vec4 nd = texture(uNormalDepth, vUv);
  if (nd.w <= 0.0) { oAO = 1.0; return; } // background
  vec3 P = texture(uPos, vUv).xyz;
  vec3 N = normalize(nd.xyz);
  vec3 rnd = vec3(texture(uNoise, vUv * uNoiseScale).xy * 2.0 - 1.0, 0.0);
  vec3 T = normalize(rnd - N * dot(rnd, N));
  vec3 B = cross(N, T);
  mat3 TBN = mat3(T, B, N);
  float oNear = occlusion(P, TBN, uNearRadius);
  float oFar = occlusion(P, TBN, uFarRadius);
  float occ = (oNear * uNearWeight + oFar * uFarWeight) / max(1e-4, uNearWeight + uFarWeight);
  oAO = pow(clamp(1.0 - occ, 0.0, 1.0), uPower);
}`;

// --- Depth-aware separable blur ---------------------------------------------
export const BLUR_FRAG = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uAO;
uniform sampler2D uNormalDepth; // for the bilateral depth weight
uniform vec2 uTexel;            // blur axis * (1 / aoResolution)
out float oAO;
void main() {
  float centerD = texture(uNormalDepth, vUv).w;
  float sum = 0.0;
  float wsum = 0.0;
  for (int i = -3; i <= 3; i++) {
    vec2 uv = vUv + uTexel * float(i);
    float d = texture(uNormalDepth, uv).w;
    float w = exp(-float(i * i) / 8.0);
    w *= step(abs(d - centerD), 0.5); // don't bleed across a depth edge
    sum += texture(uAO, uv).r * w;
    wsum += w;
  }
  oAO = sum / max(1e-4, wsum);
}`;
